import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "../../hooks/use-debounce";
import { useEnrollments } from "../../hooks/useEnrollment";
import DataTable from ".";

export default function EnrollmentTable({
  initialPage = 1,
  initialLimit = 10,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [globalFilter, setGlobalFilter] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    Number(searchParams.get("page") || initialPage),
  );
  const [limit, setLimit] = useState(
    Number(searchParams.get("limit") || initialLimit),
  );
  const [paid, setPaid] = useState(searchParams.get("paid") || undefined);
  const [courseSlug] = useState(
    searchParams.get("courseSlug") || undefined,
  );

  const debouncedFilter = useDebounce(globalFilter, 500);

  const { data, isLoading } = useEnrollments({
    page,
    limit,
    search: debouncedFilter || undefined,
    hasMadeOnboardingPayment: paid,
    courseSlug,
  });

  useEffect(() => {
    if (debouncedFilter) searchParams.set("search", debouncedFilter);
    else searchParams.delete("search");

    searchParams.set("page", String(page));
    searchParams.set("limit", String(limit));

    if (paid) searchParams.set("paid", paid);
    else searchParams.delete("paid");

    if (courseSlug) searchParams.set("courseSlug", courseSlug);
    else searchParams.delete("courseSlug");

    setSearchParams(searchParams, { replace: true });
  }, [
    debouncedFilter,
    page,
    limit,
    paid,
    courseSlug,
    searchParams,
    setSearchParams,
  ]);

  const rows = data?.data?.documents || [];

  const columns = useMemo(
    () => [
      {
        header: "NAME",
        accessorKey: "fullName",
        cell: ({ row }) => {
          const f = row.original.firstName;
          const l = row.original.lastName;
          return <span className="capitalize">{`${f} ${l}`}</span>;
        },
      },
      {
        header: "EMAIL",
        accessorKey: "email",
        cell: ({ getValue }) => <span>{getValue()}</span>,
      },
      {
        header: "PHONE",
        accessorKey: "phone",
        cell: ({ getValue }) => <span>{getValue()}</span>,
      },
      // {
      //   header: "Partner",
      //   accessorKey: "courseSlug",
      //   cell: ({ getValue }) => <span className="uppercase">{getValue()}</span>,
      // },
      {
        header: "PAID",
        accessorKey: "hasMadeOnboardingPayment",
        cell: ({ getValue, row }) => {
          const v = Boolean(getValue());

          return (
            <>
              {row.original.belongsToOrg ? (
                <span className="bg-green-100 text-green-700 uppercase px-2 py-1 rounded">
                  partner
                </span>
              ) : (
                <span
                  className={`uppercase px-2 py-1 rounded ${
                    v
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {v ? "PAID" : "UNPAID"}
                </span>
              )}
            </>
          );
        },
      },
      {
        header: "AMOUNT",
        accessorKey: "onboardingPaymentAmount",
        cell: ({ getValue }) => {
          const n = Number(getValue() || 0);
          return <span>₦{n.toLocaleString("en-NG")}</span>;
        },
      },
      {
        header: "DATE CREATED",
        accessorKey: "createdAt",
        cell: ({ getValue }) => (
          <span>{new Date(getValue()).toLocaleDateString()}</span>
        ),
      },
      {
        header: "ACTION",
        accessorKey: "action",
        cell: ({ row }) => (
          <button
            className="uppercase bg-myblue text-white px-4 py-1 rounded-md"
            onClick={() => {
              const id = row.original._id;
              const fullName = `${row.original.firstName} ${row.original.lastName}`;
              const encoded = encodeURIComponent(fullName);
              navigate(`/enrollments/${id}?name=${encoded}`);
            }}
          >
            View
          </button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="border px-3 py-2 rounded"
            placeholder="Search name, email, phone..."
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPage(1);
            }}
          />
          <select
            className="border px-3 py-2 rounded"
            value={paid ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setPaid(v === "" ? undefined : v);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="true">Paid</option>
            <option value="false">Unpaid</option>
          </select>
          {/* <input
            className="border px-3 py-2 rounded"
            placeholder="Course slug"
            value={courseSlug ?? ""}
            onChange={(e) => {
              const v = e.target.value.trim();
              setCourseSlug(v.length ? v : undefined);
              setPage(1);
            }}
          /> */}
          <select
            className="border px-3 py-2 rounded"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>

        <DataTable
          data={rows}
          columns={columns}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={limit}
          isLoading={isLoading}
          showSearchBar={false}
        />

        {/* <div className="flex items-center justify-between">
          <div className="text-sm">
            Page {pagination?.page || page} of {pagination?.pages || 1} •{" "}
            {pagination?.total || rows.length} total
          </div>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={(pagination?.page || page) <= 1 || isLoading}
            >
              Prev
            </button>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              onClick={() => setPage((p) => p + 1)}
              disabled={
                (pagination?.page || page) >= (pagination?.pages || 1) ||
                isLoading
              }
            >
              Next
            </button>
          </div>
        </div> */}
      </div>
    </>
  );
}
